/**
 * Occupancy and voids — is anything empty, and what is it costing?
 *
 * ## The line, and the sentence under it that says how it was measured
 *
 * There is no history table in this product, so occupancy over time is a **derived** figure: each
 * month is measured on its last day, against the tenancies that spanned it. A unit counts as on
 * the books that day if its record existed by then **or** one of its tenancies had already begun —
 * because the commonest way an agency starts here is to enter a portfolio they have run for years,
 * all in one afternoon, and a plain `created_at` rule would report a portfolio of nothing for
 * every month before that afternoon.
 *
 * The page says so, out loud, under the chart. A derived figure whose method is invisible is a
 * figure nobody can argue with, which is worse than one they disagree with.
 *
 * ## Lost rent is a different figure from asking rent, and both are here
 *
 * **Lost** is what the empty ones have already cost: each unit's annualised rent, pro-rata for the
 * days it has stood empty. **Asking** is what they would fetch in a year if every one of them let
 * tomorrow. The first is a debt already incurred and the second is an opportunity, and putting one
 * figure on the screen and calling it "voids" conflates them.
 */

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { monthLabel } from "@rentorng/core";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { CompositionBar, RankedBars, SERIES, SegmentKey, TrendLine } from "@/components/charts";
import { Card, CardTitle, EmptyState, Table } from "@/components/ui";
import { ChartCard, Figure, FigureRow, PeriodChoice, ReportHeader, ReportState, usePeriod, useCompactMoney } from "./shared";

interface Occupancy {
  summary: { total: number; occupied: number; vacant: number; notice: number; unavailable: number; rate: number };
  series: Array<{ key: string; units: number; occupied: number; rate: number }>;
  voids: Array<{
    id: string;
    label: string;
    propertyName: string;
    propertyId: string;
    vacantSince: string | null;
    voidDays: number | null;
    rentAmount: number | null;
  }>;
  lostRent: number;
  askingPerYear: number;
  months: number;
  asOf: string;
}

export function OccupancyReportPage() {
  const fmt = useFormat();
  const compact = useCompactMoney();
  const { vocab } = useSession();
  const [months, setMonths] = usePeriod("months", 12);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["report-occupancy", months],
    queryFn: () => api.get<Occupancy>(`/reports/occupancy?months=${months}`),
  });

  if (!data) {
    return (
      <div>
        <ReportHeader title="Occupancy and voids" question="Is anything empty, for how long, and what is it costing?" />
        <ReportState isLoading={isLoading} error={error} retry={() => refetch()} />
      </div>
    );
  }

  const full = (value: number) => fmt.money(value);

  /*
    Occupancy is measured over the **lettable** register, not over every row.

    `summariseOccupancy` divides by the units that could be let at all, so a unit under renovation
    or taken off the market is in neither half of that fraction. The composition bar below is over
    the whole register, because a state breakdown that silently dropped a state would not add up.
    The two therefore give different percentages from the same data, and both are right — which is
    precisely why each says which denominator it used rather than leaving a reader to notice a
    disagreement and distrust the page.
  */
  const lettable = data.summary.total - data.summary.unavailable;

  const states = [
    { label: "Let", value: data.summary.occupied, colour: SERIES[0] },
    { label: "On notice", value: data.summary.notice, colour: "#d97706" },
    { label: "Empty", value: data.summary.vacant, colour: "#dc2626" },
    { label: "Not available", value: data.summary.unavailable, colour: "#c4cfcd" },
  ];

  /* The longest voids, which are the ones costing the most — a unit empty three hundred days at a
     modest rent has cost more than one empty a fortnight at a large one. */
  const longest = data.voids.slice(0, 8);

  return (
    <div>
      <ReportHeader
        title="Occupancy and voids"
        question={`Is anything empty, for how long, and what is it costing?`}
        subtitle={`${data.summary.rate}% let · as at ${fmt.date(data.asOf)}`}
        actions={
          <PeriodChoice
            label="Over"
            value={months}
            onChange={setMonths}
            options={[
              { value: 6, label: "6m" },
              { value: 12, label: "12m" },
              { value: 24, label: "24m" },
            ]}
          />
        }
      />

      <div className="space-y-5">
        <FigureRow>
          <Figure
            label="Occupancy"
            value={`${data.summary.rate}%`}
            tone={data.summary.rate >= 90 ? "good" : data.summary.rate >= 75 ? "warn" : "bad"}
            sub={`${data.summary.occupied} of ${lettable} lettable ${vocab.units}`}
          />
          <Figure label="Empty" value={data.summary.vacant} tone={data.summary.vacant > 0 ? "bad" : "good"} sub="Available to let today" />
          <Figure label="Rent lost so far" value={compact(data.lostRent)} tone="bad" sub="Pro-rata, since each went empty" />
          <Figure label="Asking, per year" value={compact(data.askingPerYear)} sub="What the empty ones would fetch if let" />
        </FigureRow>

        <ChartCard
          title="Occupancy over time"
          hint="Measured on the last day of each month against the tenancies that covered it. The current month is measured today rather than at its end."
        >
          <TrendLine
            label={`Occupancy rate by month over ${data.months} months`}
            points={data.series.map((point) => ({
              label: monthLabel(point.key).split(" ")[0],
              full: monthLabel(point.key),
              value: point.rate,
              detail: `${point.occupied} of ${point.units}`,
            }))}
            ceiling={100}
            format={(value) => `${Math.round(value)}%`}
            valueColumn="Occupancy"
            colour={SERIES[1]}
            footnote={
              <>
                A {vocab.unit} counts as on the books on a day if its record existed by then, or one of its {vocab.leases}{" "}
                had already begun — so a portfolio entered in one afternoon does not read as though it appeared that day.
              </>
            }
          />
        </ChartCard>

        <div className="grid gap-5 lg:grid-cols-2">
          <ChartCard
            title={`Where every ${vocab.unit} stands today`}
            hint={`All ${data.summary.total} of them, including the ${data.summary.unavailable} that cannot be let at all — so these shares are over the whole register and the occupancy figure above is over the ${lettable} that are lettable.`}
          >
            <CompositionBar label={`${titleCase(vocab.units)} by state`} segments={states} format={(value) => String(value)} />
            <SegmentKey segments={states} format={(value) => String(value)} />
          </ChartCard>

          <ChartCard
            title="Standing empty longest"
            hint="Days empty, longest first. A long void at a modest rent has usually cost more than a short one at a large rent."
          >
            {longest.length === 0 ? (
              <EmptyState title={`Every ${vocab.unit} is let.`} />
            ) : (
              <RankedBars
                label="Empty units by days standing empty"
                rows={longest.map((row) => ({
                  label: row.label,
                  sub: row.propertyName,
                  value: row.voidDays ?? 0,
                }))}
                format={(value) => `${Math.round(value)} days`}
                valueColumn="Days empty"
                colour="#dc2626"
              />
            )}
          </ChartCard>
        </div>

        <Card padded={false}>
          <div className="p-5 sm:p-6">
            <CardTitle hint="Longest void first — the ones costing the most.">Empty {vocab.units}</CardTitle>
          </div>
          {data.voids.length === 0 ? (
            <EmptyState title={`Every ${vocab.unit} is let.`} />
          ) : (
            <Table
              head={
                <tr>
                  <th>{titleCase(vocab.unit)}</th>
                  <th>{titleCase(vocab.property)}</th>
                  <th>Empty since</th>
                  <th className="num">Days</th>
                  <th className="num">Asking</th>
                </tr>
              }
            >
              {data.voids.map((row) => (
                <tr key={row.id}>
                  <td>{row.label}</td>
                  <td>
                    <Link to={`/app/properties/${row.propertyId}`} className="link">
                      {row.propertyName}
                    </Link>
                  </td>
                  <td className="text-ink-500">{fmt.date(row.vacantSince)}</td>
                  <td className="num tabular text-danger-700">{row.voidDays ?? "—"}</td>
                  <td className="num tabular">{full(row.rentAmount ?? 0)}</td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}
